import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useSubscription } from '@/utils/hooks/use-subscription';
import { modalState, modalStore } from '@/stores/modal.store';
import { playSound } from '@/utils/playSound';
import { chatStore } from '@/stores/chat.store';
import useUserStore from '@/stores/user.store';
import AbstractAvatar from '@/components/Avatars/AbstractAvatar';
import BrandLogo from '@/components/Sidebar/BrandLogo';
import { openProfileModal } from '@/utils/profileModal';
import { useNavGameStats, formatNavMoney } from '@/utils/hooks/use-nav-game-stats';
import HoldBalance from '@/components/HoldBalance';
import { login, authUrl } from '@/utils/auth';

interface Props {
    sounds: modalState['sounds'];
    setSound: modalState['setSound'];
    isAuthed: boolean;
}

interface NavEntry {
    href: string;
    label: string;
    icon: JSX.Element;
    /** Only show to signed-in users. */
    authOnly?: boolean;
}

const iconCls =
    'w-[18px] h-[18px] shrink-0 stroke-current fill-none [stroke-width:1.8] [stroke-linecap:round] [stroke-linejoin:round]';
const pillIconCls =
    'w-4 h-4 shrink-0 stroke-current fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round]';

// Single flat nav list — rendered as a horizontally-scrollable pill strip.
const NAV: NavEntry[] = [
    {
        href: '/',
        label: 'Jackpot',
        icon: (
            <svg viewBox="0 0 24 24" className={pillIconCls}>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 3v18M3 12h18" />
            </svg>
        ),
    },
    {
        href: '/coinflip',
        label: 'Coinflip',
        icon: (
            <svg viewBox="0 0 24 24" className={pillIconCls}>
                <circle cx="12" cy="12" r="9" />
                <path d="M9 12a3 3 0 0 1 6 0" />
            </svg>
        ),
    },
    {
        href: '/casebattles',
        label: 'Case battles',
        icon: (
            <svg viewBox="0 0 24 24" className={pillIconCls}>
                <rect x="3" y="6" width="18" height="12" rx="2" />
                <path d="M3 10h18" />
            </svg>
        ),
    },
    {
        href: '/marketplace',
        label: 'Market',
        authOnly: true,
        icon: (
            <svg viewBox="0 0 24 24" className={pillIconCls}>
                <rect x="3" y="6" width="18" height="13" rx="2" />
                <circle cx="16" cy="13" r="1.5" />
            </svg>
        ),
    },
    {
        href: '/deposit',
        label: 'Deposit',
        authOnly: true,
        icon: (
            <svg viewBox="0 0 24 24" className={pillIconCls}>
                <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
        ),
    },
    {
        href: '/withdraw',
        label: 'Withdraw',
        authOnly: true,
        icon: (
            <svg viewBox="0 0 24 24" className={pillIconCls}>
                <path d="M12 19V5M5 12l7-7 7 7" />
            </svg>
        ),
    },
    {
        href: '/fairplay',
        label: 'Fairplay',
        icon: (
            <svg viewBox="0 0 24 24" className={pillIconCls}>
                <path d="M12 2l3 6 6 1-4.5 4.5L18 20l-6-3-6 3 1.5-6.5L3 9l6-1z" />
            </svg>
        ),
    },
    {
        href: '/faq',
        label: 'FAQ',
        icon: (
            <svg viewBox="0 0 24 24" className={pillIconCls}>
                <circle cx="12" cy="12" r="9" />
                <path d="M9.5 9a2.5 2.5 0 1 1 4 2c-1 .7-1.5 1.5-1.5 2.5" />
                <circle cx="12" cy="17.5" r="0.6" fill="currentColor" />
            </svg>
        ),
    },
    {
        href: '/support',
        label: 'Support',
        authOnly: true,
        icon: (
            <svg viewBox="0 0 24 24" className={pillIconCls}>
                <path d="M5 13a7 7 0 0 1 14 0" />
                <path d="M3 14a2 2 0 0 1 2-2h1v5H5a2 2 0 0 1-2-2zM21 14a2 2 0 0 0-2-2h-1v5h1a2 2 0 0 0 2-2z" />
            </svg>
        ),
    },
];

const GoldCoin = ({ size = 16 }: { size?: number }) => (
    <span
        className="grid place-items-center rounded-full shrink-0"
        style={{
            width: size,
            height: size,
            background: 'radial-gradient(circle at 35% 30%, #ffe08a, #ffc24d)',
            boxShadow: '0 0 10px rgba(255,194,77,0.7)',
        }}
    >
        <span
            className="font-black leading-none text-[#6a4500]"
            style={{ fontSize: Math.round(size * 0.6) }}
        >
            $
        </span>
    </span>
);

const GAME_HREFS = ['/', '/coinflip', '/casebattles'];

const Header = ({ sounds, setSound }: Props) => {
    const { setTitle, setComponent, toggleModal, setData } = modalStore((state) => state);
    const { toggleChat } = chatStore((state) => state);
    const { user, isAuthed, authv2 } = useUserStore((state) => state);
    const { jackpotValue, coinflipTotal, casebattleTotal } = useNavGameStats();

    const [gamesOpen, setGamesOpen] = useState(false);

    const pathname = usePathname();
    const notif = useRef<HTMLAudioElement | null>(null);
    const soundsEnabled = useRef(sounds);
    const activePill = useRef<HTMLAnchorElement | null>(null);
    const gamesBtn = useRef<HTMLButtonElement | null>(null);
    const gamesPanel = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        soundsEnabled.current = sounds;
    }, [sounds]);

    useEffect(() => {
        toggleModal(false);
        toggleChat(false);
        setGamesOpen(false);
        // Keep the current section visible in the scroll strip.
        (gameActive ? gamesBtn.current : activePill.current)?.scrollIntoView({
            inline: 'center',
            block: 'nearest',
            behavior: 'smooth',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    // Close the GAMES dropdown on an outside tap.
    useEffect(() => {
        if (!gamesOpen) return;
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (gamesBtn.current?.contains(t) || gamesPanel.current?.contains(t)) return;
            setGamesOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [gamesOpen]);

    useSubscription('notification', (data) => {
        playSound(notif, soundsEnabled.current);
        setComponent('Notification');
        setTitle(data.header);
        setData({ text: data.text, type: data.type });
        toggleModal(true);
    }).then();

    const isActive = (href: string) =>
        href === '/' ? pathname === '/' : pathname?.startsWith(href);

    const balance = isAuthed ? user.balanceUsd?.toFixed(2) ?? '0.00' : null;

    const items = NAV.filter((n) => !n.authOnly || isAuthed);
    const games = items.filter((n) => GAME_HREFS.includes(n.href));
    const others = items.filter((n) => !GAME_HREFS.includes(n.href));
    const gameActive = games.some((g) => isActive(g.href));

    // Live in-play totals per game mode (same source as the desktop nav).
    const gameTotals: Record<string, number> = {
        '/': jackpotValue,
        '/coinflip': coinflipTotal,
        '/casebattles': casebattleTotal,
    };

    return (
        <>
            <nav
                className="xl:hidden fixed top-0 left-0 right-0 z-40 flex flex-col border-b border-stroke"
                style={{
                    // Опаковый фон вместо backdrop-filter: размытие на fixed-шапке
                    // перерастеризовывалось на каждом кадре скролла (особенно
                    // заметно на мобильных) — основная причина рывков.
                    background: 'linear-gradient(180deg, rgba(30,24,58,0.97), rgba(16,12,34,0.98))',
                }}
            >
                {/* Top bar: brand + account actions */}
                <div className="h-14 flex items-center justify-between gap-2 px-3">
                    <Link href="/" className="flex items-center gap-2 min-w-0">
                        <BrandLogo size={28} idSuffix="hdr-bar" />
                        <div className="flex flex-col leading-none">
                            <span className="font-chat-nick font-extrabold text-[13px] tracking-[0.04em] whitespace-nowrap text-text_main">
                                RUSTY<span className="text-text_3 font-bold">XBET</span>
                            </span>
                            <span className="text-[9px] text-text_4 font-semibold tracking-[0.10em] mt-0.5">
                                .COM
                            </span>
                        </div>
                    </Link>

                    <div className="flex items-center gap-2">
                        {isAuthed && (
                            <Link
                                href="/deposit"
                                className="font-chat-nick inline-flex items-center gap-1.5 h-9 px-3 rounded-fl bg-layer_2 border border-stroke text-[13px] font-extrabold text-white tabular-nums active:scale-95 transition-transform"
                            >
                                <GoldCoin />
                                {balance}
                            </Link>
                        )}

                        {/* Funds on hold (only when > 0) */}
                        <HoldBalance />

                        {/* Sound toggle — sits next to the balance */}
                        <button
                            onClick={() => setSound()}
                            aria-label={sounds ? 'Mute sound' : 'Unmute sound'}
                            aria-pressed={sounds}
                            className={`w-9 h-9 grid place-items-center rounded-fl border bg-layer_2 active:scale-95 transition-all shrink-0 ${
                                sounds
                                    ? 'border-stroke text-accent'
                                    : 'border-stroke text-text_4 hover:text-text_2'
                            }`}
                        >
                            {sounds ? (
                                <svg viewBox="0 0 24 24" className={iconCls} style={{ strokeWidth: 2 }}>
                                    <path d="M11 5 6 9H3v6h3l5 4V5z" />
                                    <path d="M16 9a3.5 3.5 0 0 1 0 6M19 6a7 7 0 0 1 0 12" />
                                </svg>
                            ) : (
                                <svg viewBox="0 0 24 24" className={iconCls} style={{ strokeWidth: 2 }}>
                                    <path d="M11 5 6 9H3v6h3l5 4V5z" />
                                    <path d="M22 9l-6 6M16 9l6 6" />
                                </svg>
                            )}
                        </button>

                        {isAuthed ? (
                            <button
                                onClick={() => openProfileModal()}
                                aria-label="Profile"
                                className="w-9 h-9 grid place-items-center rounded-fl overflow-hidden border border-stroke bg-layer_2 shrink-0"
                            >
                                <AbstractAvatar
                                    seed={user.name || String(user.id)}
                                    src={user.avatar}
                                    size={36}
                                    square
                                />
                            </button>
                        ) : (
                            <Link
                                href={authUrl()}
                                onClick={(e) => {
                                    e.preventDefault();
                                    login();
                                }}
                                className={`${authv2 ? 'goAuth ' : ''}font-chat-nick inline-flex items-center h-9 px-4 rounded-fl text-white text-[12px] font-extrabold tracking-[0.06em] uppercase active:scale-95 transition-transform`}
                                style={{
                                    background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                                    boxShadow:
                                        'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 20px -4px rgba(139,109,255,0.7)',
                                }}
                            >
                                Sign in
                            </Link>
                        )}
                        <button
                            onClick={() => toggleChat()}
                            aria-label="Chat"
                            className="w-9 h-9 grid place-items-center rounded-fl bg-layer_2 border border-stroke text-text_2 hover:text-accent active:scale-95 transition-all shrink-0"
                        >
                            <svg viewBox="0 0 24 24" className={iconCls} style={{ strokeWidth: 2 }}>
                                <path d="M21 12a8 8 0 0 1-11.5 7.2L3 21l1.8-6.5A8 8 0 1 1 21 12z" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Section nav: horizontally scrollable pills */}
                <div className="h-12 flex items-center gap-2 px-3 overflow-x-auto overflow-y-hidden no-scrollbar border-t border-stroke">
                    {/* GAMES — collapses Jackpot / Coinflip / Case battles into a dropdown */}
                    <button
                        ref={gamesBtn}
                        onClick={() => setGamesOpen((v) => !v)}
                        className={`font-chat-nick shrink-0 inline-flex items-center gap-2 h-9 pl-3.5 pr-3 rounded-full text-[12.5px] font-bold tracking-[0.04em] border transition-all ${
                            gameActive
                                ? 'text-white border-transparent'
                                : 'text-text_2 bg-layer_2 border-stroke active:scale-95'
                        }`}
                        style={
                            gameActive
                                ? {
                                      background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                                      boxShadow:
                                          'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 18px -4px rgba(139,109,255,0.7)',
                                  }
                                : undefined
                        }
                    >
                        <svg viewBox="0 0 24 24" className={pillIconCls}>
                            <rect x="3" y="3" width="7" height="7" rx="1.6" />
                            <rect x="14" y="3" width="7" height="7" rx="1.6" />
                            <rect x="3" y="14" width="7" height="7" rx="1.6" />
                            <rect x="14" y="14" width="7" height="7" rx="1.6" />
                        </svg>
                        <span className="whitespace-nowrap">Games</span>
                        <svg
                            viewBox="0 0 24 24"
                            className={`w-3.5 h-3.5 shrink-0 stroke-current fill-none [stroke-width:2.4] [stroke-linecap:round] [stroke-linejoin:round] transition-transform ${
                                gamesOpen ? 'rotate-180' : ''
                            }`}
                        >
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>

                    {others.map((n) => {
                        const active = isActive(n.href);
                        return (
                            <Link
                                key={n.href}
                                href={n.href}
                                ref={active ? activePill : undefined}
                                className={`font-chat-nick shrink-0 inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-[12.5px] font-bold tracking-[0.04em] border transition-all ${
                                    active
                                        ? 'text-white border-transparent'
                                        : 'text-text_2 bg-layer_2 border-stroke active:scale-95'
                                }`}
                                style={
                                    active
                                        ? {
                                              background:
                                                  'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                                              boxShadow:
                                                  'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 18px -4px rgba(139,109,255,0.7)',
                                          }
                                        : undefined
                                }
                            >
                                {n.icon}
                                <span className="whitespace-nowrap">{n.label}</span>
                            </Link>
                        );
                    })}
                </div>

                {/* GAMES dropdown panel (anchored under the header, outside the scroll strip) */}
                {gamesOpen && (
                    <div
                        ref={gamesPanel}
                        className="absolute top-full left-3 mt-1.5 z-50 w-56 p-1.5 rounded-fl-md flex flex-col gap-1 animate-fluent-fadeup"
                        style={{
                            background:
                                'linear-gradient(180deg, rgba(30,24,58,0.98), rgba(16,12,34,0.99))',
                            border: '1px solid color-mix(in srgb, #8b6dff 34%, transparent)',
                            boxShadow:
                                '0 22px 50px -16px rgba(0,0,0,0.72), 0 0 44px -14px rgba(139,109,255,0.55)',
                        }}
                    >
                        {games.map((g) => {
                            const active = isActive(g.href);
                            return (
                                <Link
                                    key={g.href}
                                    href={g.href}
                                    onClick={() => setGamesOpen(false)}
                                    className={`font-chat-nick flex items-center gap-2.5 px-3 py-2.5 rounded-fl text-[13px] font-bold transition-colors ${
                                        active
                                            ? 'text-white'
                                            : 'text-text_2 hover:bg-layer_3 hover:text-white'
                                    }`}
                                    style={
                                        active
                                            ? {
                                                  background:
                                                      'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                                                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
                                              }
                                            : undefined
                                    }
                                >
                                    {g.icon}
                                    <span className="whitespace-nowrap">{g.label}</span>
                                    {gameTotals[g.href] > 0 && (
                                        <span
                                            className="font-chat-nick ml-auto text-[11px] font-extrabold px-2 py-0.5 rounded-md tabular-nums"
                                            style={{
                                                color: '#ffe0a3',
                                                background: 'rgba(255,194,77,0.1)',
                                                border: '1px solid rgba(255,194,77,0.45)',
                                                boxShadow:
                                                    '0 0 12px -1px rgba(255,194,77,0.7), inset 0 0 8px -3px rgba(255,194,77,0.6)',
                                                textShadow: '0 0 8px rgba(255,194,77,0.6)',
                                            }}
                                        >
                                            {formatNavMoney(gameTotals[g.href])}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                    </div>
                )}
            </nav>
        </>
    );
};

export default Header;
